import { trackEvent } from './posthog'

// Initialize metrics tracking
export const initMetricsTracking = () => {
  // Track page performance
  if ('performance' in window) {
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType(
        'navigation'
      )[0] as PerformanceNavigationTiming
      if (navigation) {
        trackEvent('page.loaded', {
          page_name: window.location.pathname,
          duration_ms: navigation.loadEventEnd - navigation.fetchStart,
          dns_time: navigation.domainLookupEnd - navigation.domainLookupStart,
          tcp_time: navigation.connectEnd - navigation.connectStart,
          request_time: navigation.responseEnd - navigation.requestStart,
          response_time: navigation.responseEnd - navigation.responseStart,
          dom_processing: navigation.domComplete - navigation.domInteractive,
          device_type: getDeviceType(),
        })
      }
    })
  }

  // Track user engagement
  let engagementStartTime = Date.now()
  let isEngaged = true

  const trackEngagement = () => {
    if (isEngaged) {
      const engagementDuration = (Date.now() - engagementStartTime) / 1000
      trackEvent('user.engagement', {
        duration_seconds: engagementDuration,
        page: window.location.pathname,
      })
    }
  }

  // Track when user leaves
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      isEngaged = false
      trackEngagement()
    } else {
      isEngaged = true
      engagementStartTime = Date.now()
    }
  })

  // Track before unload
  window.addEventListener('beforeunload', trackEngagement)
}

// Helper to determine device type
const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  const width = window.innerWidth
  if (width < 768) {
    return 'mobile'
  }
  if (width < 1024) {
    return 'tablet'
  }
  return 'desktop'
}

// Business metric tracking helpers
export const trackUserLogin = (userDetails: {
  userId: string
  userType: 'corporate' | 'coach' | 'member' | 'admin'
  role: string
  siteId: string
  loginMethod: 'email' | 'sso' | 'mobile'
}) => {
  trackEvent('user.logged_in', {
    user_id: userDetails.userId,
    user_type: userDetails.userType,
    role: userDetails.role,
    site_id: userDetails.siteId,
    login_method: userDetails.loginMethod,
  })
}

export const trackCheckIn = (checkInDetails: {
  memberId: string
  siteId: string
  checkInMethod: 'qr' | 'nfc' | 'manual' | 'app'
  classId?: string
  instructorId?: string
}) => {
  trackEvent('member.check_in', {
    member_id: checkInDetails.memberId,
    site_id: checkInDetails.siteId,
    check_in_method: checkInDetails.checkInMethod,
    class_id: checkInDetails.classId,
    instructor_id: checkInDetails.instructorId,
  })
}

export const trackClassBooked = (bookingDetails: {
  classId: string
  siteId: string
  memberId: string
  classType: string
  instructorId: string
  spotsRemaining: number
  bookingMethod: 'app' | 'web' | 'front_desk'
}) => {
  trackEvent('class.booked', {
    class_id: bookingDetails.classId,
    site_id: bookingDetails.siteId,
    member_id: bookingDetails.memberId,
    class_type: bookingDetails.classType,
    instructor_id: bookingDetails.instructorId,
    spots_remaining: bookingDetails.spotsRemaining,
    booking_method: bookingDetails.bookingMethod,
  })
}

export const trackFeatureUsed = (featureDetails: {
  featureName: string
  module: string
  siteId: string
  userId: string
}) => {
  trackEvent('feature_used', {
    feature_name: featureDetails.featureName,
    module: featureDetails.module,
    site_id: featureDetails.siteId,
    user_id: featureDetails.userId,
  })
}

export const trackOnboardingStep = (stepDetails: {
  corporateId: string
  step: 'site_setup' | 'staff_added' | 'schedule_created' | 'payment_setup'
  timeToCompleteMinutes: number
}) => {
  trackEvent('corporate.onboarding_step_completed', {
    corporate_id: stepDetails.corporateId,
    step: stepDetails.step,
    time_to_complete_minutes: stepDetails.timeToCompleteMinutes,
  })
}

export const trackOnboardingCompleted = (onboardingDetails: {
  corporateId: string
  siteId: string
  totalDurationDays: number
  completedSteps: string[]
}) => {
  trackEvent('corporate.onboarding_completed', {
    corporate_id: onboardingDetails.corporateId,
    site_id: onboardingDetails.siteId,
    total_duration_days: onboardingDetails.totalDurationDays,
    completed_steps: onboardingDetails.completedSteps,
  })
}

export const trackRevenueEvent = (revenueDetails: {
  siteId: string
  eventType: 'subscription_created' | 'subscription_changed' | 'subscription_cancelled'
  planType?: 'basic' | 'premium' | 'unlimited'
  mrrAmount?: number
  previousPlan?: string
  newPlan?: string
  mrrChange?: number
  changeReason?: string
}) => {
  const eventName = `revenue.${revenueDetails.eventType}`
  trackEvent(eventName, {
    site_id: revenueDetails.siteId,
    plan_type: revenueDetails.planType,
    mrr_amount: revenueDetails.mrrAmount,
    previous_plan: revenueDetails.previousPlan,
    new_plan: revenueDetails.newPlan,
    mrr_change: revenueDetails.mrrChange,
    change_reason: revenueDetails.changeReason,
  })
}

export const trackCSATScore = (csatDetails: {
  userId: string
  score: number // 1-10
  feedback?: string
  context: 'post_class' | 'monthly_survey' | 'support_ticket'
}) => {
  trackEvent('csat.score_submitted', {
    user_id: csatDetails.userId,
    score: csatDetails.score,
    feedback: csatDetails.feedback,
    context: csatDetails.context,
  })
}
